import React, { memo, useCallback, useRef } from 'react'
import { AgGridReact } from '@ag-grid-community/react'
import type {
  CellClickedEvent,
  ColDef,
  FirstDataRenderedEvent,
  GetContextMenuItemsParams,
  GetRowIdFunc,
  GridReadyEvent,
  MenuItemDef,
  ModelUpdatedEvent,
  PaginationChangedEvent,
} from '@ag-grid-community/core'
import { InboxOutlined } from '@ant-design/icons'
import styles from '../ReviewPlanSnapshot.module.scss'

const LoadingCell = () => <span>Loading...</span>

const NoRowsOverlay = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 8,
      color: '#8c8c8c',
    }}
  >
    <InboxOutlined style={{ fontSize: 40 }} />
    <span style={{ fontSize: 14 }}>No records found</span>
  </div>
)

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 500, 1000]

const DEFAULT_COL_DEF: ColDef = {
  sortable: false,
  filter: false,
  resizable: true,
}

interface PlanReviewGridProps {
  columnDefs: ColDef[]
  onGridReady: (params: GridReadyEvent) => void
  onSortChanged?: () => void
  onPageSizeChange?: (size: number) => void
  onPageChange?: (page: number) => void
  onCellClicked?: (event: CellClickedEvent) => void
  onModelUpdated?: (event: ModelUpdatedEvent) => void
  onFirstDataRendered?: (event: FirstDataRenderedEvent) => void
  pageSize?: number
  getRowId?: GetRowIdFunc
}

function PlanReviewGrid({
  columnDefs,
  onGridReady,
  onSortChanged,
  onPageSizeChange,
  onPageChange,
  onCellClicked,
  onModelUpdated,
  onFirstDataRendered,
  pageSize = 20,
  getRowId,
}: PlanReviewGridProps) {
  const prevPageSizeRef = useRef(pageSize)

  const handlePaginationChanged = useCallback(
    (event: PaginationChangedEvent) => {
      const newSize = event.api.paginationGetPageSize()
      if (newSize !== prevPageSizeRef.current) {
        prevPageSizeRef.current = newSize
        onPageSizeChange?.(newSize)
      } else if (event.newPage) {
        onPageChange?.(event.api.paginationGetCurrentPage() + 1)
      }
    },
    [onPageSizeChange, onPageChange]
  )

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams): (MenuItemDef | string)[] => {
      const copyCell: MenuItemDef = {
        name: 'Copy Cell',
        icon: '<span class="ag-icon ag-icon-copy"></span>',
        action: () => {
          const raw = params.value
          const text = raw === null || raw === undefined ? '' : String(raw)
          navigator.clipboard.writeText(text)
        },
      }
      return [copyCell]
    },
    []
  )

  return (
    <div className={`ag-theme-alpine ${styles.gridContainer}`}>
      <AgGridReact
        pagination={true}
        paginationPageSize={pageSize}
        cacheBlockSize={pageSize}
        rowModelType="serverSide"
        onGridReady={onGridReady}
        columnDefs={columnDefs}
        onSortChanged={onSortChanged}
        onPaginationChanged={handlePaginationChanged}
        tooltipShowDelay={300}
        tooltipHideDelay={5000}
        suppressRowClickSelection={true}
        onCellClicked={onCellClicked}
        onModelUpdated={onModelUpdated}
        onFirstDataRendered={onFirstDataRendered}
        paginationPageSizeSelector={PAGE_SIZE_OPTIONS}
        loadingCellRenderer={LoadingCell}
        suppressLoadingOverlay={true}
        noRowsOverlayComponent={NoRowsOverlay}
        defaultColDef={DEFAULT_COL_DEF}
        getContextMenuItems={getContextMenuItems}
        getRowId={getRowId}
        rowHeight={36}
      />
    </div>
  )
}

export default memo(PlanReviewGrid)
